import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { SparkleRainCanvas } from '@/components/SparkleRainCanvas';
import { CursorTrail } from '@/components/CursorTrail';
import { BackgroundGradient } from '@/components/BackgroundGradient';
import { SplashScreen } from '@/components/SplashScreen';
import { ChatBubble } from '@/components/ChatBubble';
import { Composer } from '@/components/Composer';
import { DateSeparator } from '@/components/DateSeparator';
import { http } from '@/lib/api';
import { getDeviceId } from '@/lib/deviceId';
import { whichDateChanged } from '@/lib/formatTimes';
import {
  clearBadge,
  registerServiceWorker,
  isPushSupported,
  isStandalonePWA,
  shouldShowNotificationPrompt,
  isDevNotificationUI,
  markNotificationPromptPending,
  clearNotificationPromptPending,
  isAlreadySubscribed,
  requestNotificationPermission,
  activatePushSubscription,
  syncPushSubscriptionIfGranted,
  showLocalMessageNotification,
  unsubscribeFromPush,
} from '@/lib/pushNotifications';
import { CHAT } from '@/constants/testIds';
import {
  playReceived,
  playTranslation,
  primeAudioOnFirstGesture,
} from '@/lib/sounds';
import { useTimeOfDayTheme } from '@/lib/theme';
import { useStableViewportHeight } from '@/lib/useStableViewportHeight';
import { isLiteMode, shouldSkipCanvases } from '@/lib/perfMode';

const POSTS_CACHE_KEY = 'danya-rachel-posts-cache-v1';

function readCachedPosts() {
  try {
    const raw = localStorage.getItem(POSTS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (_e) {
    return null;
  }
}

function writeCachedPosts(posts) {
  try {
    localStorage.setItem(POSTS_CACHE_KEY, JSON.stringify(posts));
  } catch (_e) {
    /* quota or disabled storage — ignore */
  }
}

function useDeviceId() {
  const [id] = useState(() => getDeviceId());
  return id;
}

function computeGlowingIds(posts) {
  // Glow the newest contiguous run (ending at the last post) from same device_id.
  if (!posts || posts.length === 0) return new Set();
  const last = posts[posts.length - 1];
  const targetDevice = last.device_id;
  const ids = new Set();
  for (let i = posts.length - 1; i >= 0; i -= 1) {
    if (posts[i].device_id === targetDevice) {
      ids.add(posts[i].id);
    } else {
      break;
    }
  }
  return ids;
}

function Home() {
  // Two independent perf flags — see /app/frontend/src/lib/perfMode.js.
  //   liteMode: explicit `?lite=1` → adds CSS overrides (also implies
  //             skipCanvases). Used by automated smoke tests.
  //   skipCanvases: any headless UA OR liteMode → don't mount the
  //             expensive 60fps canvases. Lets me debug VISUAL issues
  //             in Playwright with the production background stack
  //             intact, since real Chromium without GPU acceleration
  //             OOM-crashes on the sparkle + cursor canvases.
  const [liteMode] = useState(() => isLiteMode());
  const [skipCanvases] = useState(() => shouldSkipCanvases());
  useEffect(() => {
    if (liteMode && typeof document !== 'undefined') {
      document.documentElement.classList.add('lite-mode');
    }
    return undefined;
  }, [liteMode]);

  // Lazy-init from localStorage so the feed shows INSTANTLY on every launch,
  // long before the first network fetch resolves.
  const [posts, setPosts] = useState(() => readCachedPosts() || []);
  const [loading, setLoading] = useState(() => readCachedPosts() === null);
  const deviceId = useDeviceId();
  const feedEndRef = useRef(null);
  const scrolledByUserRef = useRef(false);
  // Tracks whether the per-session "cinematic scroll" intro animation
  // (snap-to-top → smooth fast-forward to bottom) has already played.
  // It is intentionally NOT persisted — every fresh PWA launch replays it.
  const cinematicPlayedRef = useRef(false);

  // Notification permission state — drives whether we show the small
  // "enable notifications" pill below the title. Once it transitions to
  // 'granted' or 'denied', the button hides forever and the layout is
  // back to the original look.
  const [notifPermission, setNotifPermission] = useState(() => {
    if (typeof window === 'undefined' || typeof Notification === 'undefined') {
      return 'unsupported';
    }
    return Notification.permission || 'default';
  });
  const [notifBusy, setNotifBusy] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const [notifUiTick, setNotifUiTick] = useState(0);

  // Identity picker has been entirely removed — posts are anonymous and
  // bubble alignment (left vs right) is decided by device_id only. The
  // app's display name is always "my love" everywhere it appears (install
  // dialog, home-screen icon, push-notification banner). See
  // /app/frontend/public/manifest.json and /app/frontend/public/index.html
  // for the canonical sources of that name.

  // Dev: always show enable/disable. Production: one-time enable until subscribed.
  const showNotifBtn =
    typeof Notification !== 'undefined' &&
    (isDevNotificationUI()
      ? true
      : !alreadySubscribed && shouldShowNotificationPrompt());
  const notifBtnLabel = alreadySubscribed
    ? notifBusy
      ? 'disabling…'
      : 'disable notifications'
    : notifBusy
      ? 'enabling…'
      : 'enable notifications';
  // notifUiTick forces re-eval after async permission / PWA sync.
  void notifUiTick;

  const onDisableNotifications = async () => {
    if (notifBusy) return;
    setNotifBusy(true);
    try {
      await unsubscribeFromPush(deviceId);
      setAlreadySubscribed(false);
      toast.success('notifications disabled', { duration: 6000 });
    } catch (e) {
      toast.error(`couldn't disable: ${e?.message || e}`, { duration: 10000 });
    } finally {
      setNotifBusy(false);
    }
  };

  const onEnableNotifications = async () => {
    if (notifBusy) return;
    if (typeof Notification === 'undefined') return;
    setNotifBusy(true);
    let ok = false;
    let errMessage = null;
    try {
      // Step 1: native permission sheet FIRST (Chrome / iOS / Safari).
      const perm = await requestNotificationPermission();
      setNotifPermission(perm);
      if (perm !== 'granted') {
        if (perm === 'denied') {
          toast.error(
            'notifications blocked — in Chrome: lock icon → site settings → reset notifications',
            { duration: 8000 },
          );
        }
        return;
      }

      // Step 2: user allowed — auto-register SW, subscribe, sync backend.
      ok = await activatePushSubscription(deviceId, { force: true });
    } catch (e) {
      errMessage = e?.message || String(e);
    } finally {
      const finalPerm =
        typeof Notification !== 'undefined' ? Notification.permission || 'default' : 'unsupported';
      setNotifPermission(finalPerm);
      setNotifBusy(false);
    }

    if (ok) {
      clearNotificationPromptPending();
      setAlreadySubscribed(true);
      // Fire a real test push and report what Apple/Mozilla/Google said.
      // The banner mirrors a real-post layout (title = "my love", body =
      // "notifications enabled") so users see the exact format they'll
      // get for genuine posts.
      let attempt = 0;
      let testData = null;
      while (attempt < 2) {
        attempt += 1;
        try {
          const r = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/push/test/${encodeURIComponent(deviceId)}`,
            { method: 'POST' },
          );
          testData = await r.json().catch(() => ({}));
          if (testData.ok) break;
          // If APS rejected with 400/410, the subscription is dead — force
          // a fresh resubscribe and try ONE more time.
          if ([400, 410].includes(testData.status) && attempt === 1) {
            try {
              await activatePushSubscription(deviceId, { force: true });
            } catch (_e) {
              break;
            }
            continue;
          }
          break;
        } catch (e) {
          testData = { ok: false, error: e?.message || String(e) };
          break;
        }
      }
      if (testData?.ok) {
        toast.success('notifications enabled 🖤', { duration: 4000 });
      }
    } else if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
      clearNotificationPromptPending();
      // Chrome will not re-prompt once denied — no instruction toast; user
      // must reset via browser site settings on their own.
    } else if (errMessage) {
      toast.error(`couldn't enable notifications: ${errMessage}`, { duration: 6000 });
    }
  };

  // Prime the audio pipeline on the very first user gesture anywhere
  // inside the document. This is required by iOS: HTMLAudioElement.play()
  // can only run for the first time inside a user-gesture handler. After
  // a single primed play, subsequent triggers (including passive ones
  // like "post received via polling" or "translation arrived") will
  // play normally. The helper installs a once-only document listener
  // and is safe to call repeatedly — duplicates are deduped internally.
  useEffect(() => {
    primeAudioOnFirstGesture();
    return undefined;
  }, []);

  // Scroll-lock during composer input focus — REMOVED.
  //
  // Previously this hook listened for `focusin` on the composer and
  // set `.app-shell.style.overflow = 'hidden'` to block scroll while
  // the keyboard was open. Two motivations historically:
  //   1. Block iOS Safari's auto-scroll-on-focus (which used to drag
  //      the page around).
  //   2. Belt-and-braces against the visualViewport-based JS keyboard
  //      offset getting out of sync with content scroll.
  //
  // Both motivations are obsolete now:
  //   • With `interactive-widget=resizes-content` (see index.html), iOS
  //     shrinks the layout viewport itself — there's no document-level
  //     auto-scroll to defend against.
  //   • The visualViewport keyboard-offset JS hook is gone (see the
  //     comment block on the next effect down).
  //
  // The user was complaining that scroll didn't work while typing —
  // this hook was exactly why. With the hook gone, the user can scroll
  // the chat-feed freely while the keyboard is open, and the composer
  // can no longer drift (because its position isn't being touched by
  // any JS).
  useEffect(() => {
    return undefined;
  }, []);

  // Keyboard avoidance is now handled NATIVELY by iOS via the
  // `interactive-widget=resizes-content` viewport meta tag (see
  // /app/frontend/public/index.html). When the iOS keyboard appears,
  // iOS shrinks the LAYOUT VIEWPORT itself, which shrinks `.app-frame`
  // (position: fixed; inset: 0), which shrinks `.app-shell` (flex:
  // 1 1 auto) — and the composer (flex: 0 0 auto) ends up flush
  // against the keyboard top automatically.
  //
  // No JS hook required. No `--kb-offset` to manage. No visualViewport
  // listeners. No transform tricks. This is the SINGLE biggest source
  // of "composer floats around the screen" reports gone — those bugs
  // came from JS that updated layout in response to visualViewport
  // resize / scroll events during touch gestures, and iOS Safari's
  // compositor would smear those updates visibly.
  //
  // env(safe-area-inset-bottom) is handled directly in .composer-wrap's
  // padding-bottom — iOS reports 0 for it when the keyboard is open
  // (because the home indicator zone is covered by the keyboard) and
  // ~34 px when it's closed, so the composer pill auto-clears the home
  // indicator without any JS.
  useEffect(() => {
    // Intentionally empty — kept as a structural anchor so future
    // engineers don't try to re-introduce a visualViewport keyboard
    // hook here without reading the comment block above first.
    return undefined;
  }, []);

  // Live-measure the composer-wrap height so the chat feed's bottom
  // padding can be exactly `--gap-feed` taller than the composer —
  // making the gap between the newest post and the composer visually
  // identical to the gap between any two adjacent posts. Reacts to:
  //   • initial layout
  //   • attachment strip appearing / disappearing
  //   • audio recording UI expanding the composer
  //   • multi-line text growing the input box
  //   • safe-area-inset changes (e.g. orientation flip)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const composer = document.querySelector('.composer-wrap');
    if (!composer) return undefined;
    const root = document.documentElement;
    const update = () => {
      const h = composer.getBoundingClientRect().height;
      // Round to whole pixels to avoid sub-pixel jitter in the feed
      // bottom-padding on every resize tick.
      root.style.setProperty('--composer-h', `${Math.round(h)}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(composer);
    window.addEventListener('orientationchange', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  // Track PWA / prompt eligibility reactively (iOS can report standalone late).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const sync = () => {
      if (isStandalonePWA()) markNotificationPromptPending();
      setNotifUiTick((n) => n + 1);
    };
    sync();
    const mqs = ['standalone', 'fullscreen', 'minimal-ui'].map((mode) =>
      window.matchMedia?.(`(display-mode: ${mode})`),
    );
    mqs.forEach((mq) => mq?.addEventListener?.('change', sync));
    return () => {
      mqs.forEach((mq) => mq?.removeEventListener?.('change', sync));
    };
  }, []);

  useEffect(() => {
    if (!deviceId) return undefined;
    let cancelled = false;
    isAlreadySubscribed().then((v) => {
      if (!cancelled) setAlreadySubscribed(v);
    });
    return () => {
      cancelled = true;
    };
  }, [deviceId, notifPermission]);

  // Register the service worker on mount and clear any pending badge.
  // The SW handles incoming push notifications and OS badge updates.
  // The explicit "enable notifications" pill is the ONLY place we ask for
  // permission when it is still 'default' — no silent first-gesture prompt.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // In any headless / lite context we skip ALL of: service-worker
    // registration, push subscription, badge clearing, and the
    // visibilitychange listener that triggers them. They're remaining
    // sources of background work that could pressure a software-rendered
    // headless Chromium beyond its memory budget.
    if (skipCanvases) return undefined;
    registerServiceWorker().catch(() => {});

    const syncPermission = () => {
      if (typeof Notification !== 'undefined') {
        const perm = Notification.permission || 'default';
        setNotifPermission(perm);
        if (perm !== 'default') clearNotificationPromptPending();
      }
      setNotifUiTick((n) => n + 1);
      isAlreadySubscribed().then(setAlreadySubscribed).catch(() => {});
    };

    // Whenever the app becomes visible (open from background or tab switch),
    // clear the unread badge for this device AND re-sync notification
    // permission state so the pill hides if the user enabled it via iOS
    // Settings while the app was backgrounded.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        clearBadge(deviceId).catch(() => {});
        syncPermission();
      }
    };
    onVisible();
    document.addEventListener('visibilitychange', onVisible);

    // SILENT path: permission already granted — always re-sync the browser
    // subscription to the backend (browser-only checks are not enough; the
    // server must have the record or send_push_to_others sends to nobody).
    syncPushSubscriptionIfGranted(deviceId)
      .then((ok) => {
        if (ok) setAlreadySubscribed(true);
      })
      .catch(() => {});

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [deviceId, skipCanvases]);

  // Ref pointing at the most-recent `load` function. Used by the SW
  // message listener (further below) to trigger an instant refetch when
  // a push arrives — instead of waiting for the next 8s poll tick.
  const loadRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    let firstLoadDone = false;
    // Counter for consecutive "this poll returned fewer posts than I have
    // cached" responses. After two of these in a row (~16s), we trust the
    // backend and let the cache shrink — this lets genuine wipes propagate
    // without sacrificing protection against transient network blips.
    let suspiciousShrinkCount = 0;
    async function load() {
      try {
        const resp = await http.get('/posts');
        if (!mounted) return;
        const fresh = resp.data || [];
        const cached = readCachedPosts() || [];

        // Defensive merge: if the new response is SHORTER than the cache,
        // be suspicious — most likely cause is a transient network/backend
        // hiccup that we shouldn't propagate to the UI.  After 2 consecutive
        // such responses though, trust it (real wipes, real deletions).
        if (fresh.length < cached.length && cached.length > 0) {
          suspiciousShrinkCount += 1;
          if (suspiciousShrinkCount < 2) {
            // Show cached posts the first time we encounter this.
            if (posts.length === 0) setPosts(cached);
            firstLoadDone = true;
            return;
          }
          // Two consecutive shrinks — accept the backend's truth.
        } else {
          suspiciousShrinkCount = 0;
        }

        setPosts(fresh);
        writeCachedPosts(fresh);
        firstLoadDone = true;
      } catch (_e) {
        // Network/HTTP error: fall back to the cache if we haven't shown
        // anything yet, otherwise leave the existing in-memory posts alone.
        if (mounted && !firstLoadDone) {
          const cached = readCachedPosts() || [];
          setPosts(cached);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadRef.current = load;
    load();
    // Light polling for cross-device updates (no websockets in v1).
    const t = setInterval(load, 8000);
    return () => {
      mounted = false;
      clearInterval(t);
      loadRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for `push-received` messages from the service worker. The SW
  // sends these for EVERY incoming push, including ones whose visible
  // banner it suppressed because the app is currently focused. Triggering
  // an instant feed refresh makes the new post pop in immediately rather
  // than after the next 8s poll — so the chat feels real-time even when
  // the OS banner is hidden.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return undefined;
    }
    const onSwMessage = (event) => {
      const data = event && event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'push-received') {
        // Fire-and-forget; loadRef will be null only briefly during HMR.
        try {
          loadRef.current && loadRef.current();
        } catch (_e) {
          /* no-op */
        }
      }
    };
    navigator.serviceWorker.addEventListener('message', onSwMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onSwMessage);
    };
  }, []);

  // Get the actual scroll container. Since iOS PWA overscroll on the
  // DOCUMENT exposes the solid `--bg-3` background at the top/bottom
  // ("green band on overscroll up" bug), html / body have `overflow:
  // hidden` and scroll has been moved into `.app-shell`. All scroll
  // operations must operate on that element, NOT on `window`.
  const getScrollEl = () => document.querySelector('.app-shell');

  // Reusable "fast scroll to bottom" — 250 ms ease-out cubic on the
  // .app-shell scrollTop. Used both for the initial cinematic intro AND
  // every time THIS device sends a new message. Returns a cleanup function.
  const fastScrollToBottom = (allowCancel = true) => {
    const sc = getScrollEl();
    if (!sc) return () => {};
    const startY = sc.scrollTop || 0;
    const targetY = Math.max(0, sc.scrollHeight - sc.clientHeight);
    const distance = targetY - startY;
    if (distance <= 0) return () => {};

    const duration = 250;
    const t0 = performance.now();
    let cancelled = false;

    const cancel = () => {
      cancelled = true;
    };
    if (allowCancel) {
      sc.addEventListener('touchstart', cancel, { passive: true, once: true });
      sc.addEventListener('wheel', cancel, { passive: true, once: true });
    }

    const step = (now) => {
      if (cancelled) return;
      const elapsed = now - t0;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      sc.scrollTop = startY + distance * eased;
      if (t < 1) {
        requestAnimationFrame(step);
      } else if (allowCancel) {
        sc.removeEventListener('touchstart', cancel);
        sc.removeEventListener('wheel', cancel);
      }
    };
    requestAnimationFrame(step);

    return () => {
      cancel();
      if (allowCancel) {
        sc.removeEventListener('touchstart', cancel);
        sc.removeEventListener('wheel', cancel);
      }
    };
  };

  // Helper: is the user currently within `tolerance` px of the bottom?
  // Used both for the "other side just posted" auto-scroll rule and the
  // "own post just got its translation populated" rule.
  const isNearBottom = (tolerance = 200) => {
    const sc = getScrollEl();
    if (!sc) return true;
    return sc.scrollHeight - sc.scrollTop - sc.clientHeight < tolerance;
  };

  // Helper: is the user "within N messages of the bottom"? We measure
  // the top offset of the Nth-from-last chat bubble in the live DOM and
  // check whether the user's current scroll position has scrolled past
  // it. Used by the received-message auto-scroll rule: scroll for ALL
  // received messages UNLESS the user is more than 10 messages up.
  const isWithinNMessagesFromBottom = (n = 10) => {
    const sc = getScrollEl();
    if (!sc) return true;
    // Bubbles carry data-testid="chat-post-bubble" on every post (see
    // /app/frontend/src/constants/testIds/chat.js → postBubble).
    const bubbles = document.querySelectorAll(
      '[data-testid="chat-post-bubble"]',
    );
    if (bubbles.length === 0) return true;
    if (bubbles.length <= n) return true; // not enough posts to be "10 up"

    const targetIdx = bubbles.length - 1 - n; // the Nth-from-last bubble
    const targetEl = bubbles[targetIdx];
    if (!targetEl) return true;
    const targetTop = targetEl.getBoundingClientRect().top + sc.scrollTop;
    // User counts as "within N from bottom" if their visible window
    // already reaches that target bubble.
    return sc.scrollTop + sc.clientHeight >= targetTop;
  };

  const newestPostDeviceId =
    posts.length > 0 ? posts[posts.length - 1].device_id : null;

  useEffect(() => {
    // Scroll behavior on post-list LENGTH changes.
    //
    //   • First non-empty render this session → cinematic intro
    //     (snap to top → fast scroll to bottom).
    //   • Self-send (last post's device_id matches this device) →
    //     always fast-scroll to the bottom.
    //   • Other-side post → only auto-scroll if the user is near the
    //     bottom already; if they're up reading older messages, leave
    //     them where they are.
    if (!feedEndRef.current) return undefined;
    if (posts.length === 0) return undefined;

    if (!cinematicPlayedRef.current) {
      cinematicPlayedRef.current = true;
      scrolledByUserRef.current = false;
      const sc = getScrollEl();
      if (sc) sc.scrollTop = 0;
      const startTimer = setTimeout(() => {
        fastScrollToBottom(true);
      }, 100);
      return () => clearTimeout(startTimer);
    }

    if (newestPostDeviceId === deviceId) {
      // Tiny delay so the new bubble has actually been laid out before
      // we measure scrollHeight; without this the target can be stale.
      const t = setTimeout(() => fastScrollToBottom(false), 30);
      return () => clearTimeout(t);
    }
    if (newestPostDeviceId && newestPostDeviceId !== deviceId) {
      // Other-side post just arrived (either via polling or via the
      // SW push-received nudge). Fire the receive sound first — the
      // marimba ding. No-op when iOS ring switch is off.
      playReceived();
      // Tab hidden + no OS push (HTTP / unsubscribed): show a local banner.
      showLocalMessageNotification(1);
      // Then apply the auto-scroll rule the user actually wants:
      //   SCROLL TO BOTTOM for ALL received messages, except when the
      //   user is reading 10+ messages up in history (they're clearly
      //   intentionally scrolled away and we shouldn't yank them).
      if (isWithinNMessagesFromBottom(10)) {
        const t = setTimeout(() => fastScrollToBottom(false), 30);
        return () => clearTimeout(t);
      }
    }
    return undefined;
    // Scroll helpers are DOM utilities; only re-run when post count / sender changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length, deviceId, newestPostDeviceId]);

  // SEPARATE effect: detect when ANY post's translation populates
  // asynchronously (typically ~2–3 s after the post is created). When
  // that happens the bubble grows downward — if it grew past the visible
  // area the new translation ends up hidden behind the composer / off
  // the bottom of the screen. Re-scroll if the user is still near the
  // bottom. We skip the very first run so we don't auto-scroll on the
  // cached posts that were already translated long ago.
  //
  // This intentionally tracks BOTH own and counterpart posts: previously
  // we filtered to `device_id === deviceId` (own-only), which meant the
  // sender saw their bubble auto-snap once the LLM translation arrived
  // but the recipient's view never re-scrolled when the same translation
  // landed on the counterpart's bubble. Both sides need the same
  // behavior — when a translation pops in and the user is glued to the
  // bottom, keep them glued.
  const allTranslationsRef = useRef(null);
  useEffect(() => {
    const snapshot = {};
    for (const p of posts) {
      snapshot[p.id] = !!(p.translation && p.translation.trim());
    }
    const prev = allTranslationsRef.current;
    allTranslationsRef.current = snapshot;

    if (prev == null) return; // first run — just record state, don't scroll.

    let justTranslated = false;
    for (const id of Object.keys(snapshot)) {
      if (snapshot[id] && !prev[id]) {
        justTranslated = true;
        break;
      }
    }
    if (justTranslated) {
      // Translation just popped in — fire the lighter "piano only"
      // sound (no swoosh, a slightly higher pitch to differentiate
      // it from a fresh post). No-op when sound is toggled off.
      playTranslation();
    }
    if (justTranslated && cinematicPlayedRef.current && isNearBottom(220)) {
      // Defer a couple frames so the bubble has been re-laid out at its
      // new (taller) height before we measure scrollHeight.
      const t = setTimeout(() => fastScrollToBottom(false), 60);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, deviceId]);

  useEffect(() => {
    function onScroll() {
      scrolledByUserRef.current = true;
    }
    window.addEventListener('wheel', onScroll, { passive: true });
    window.addEventListener('touchmove', onScroll, { passive: true });
    return () => {
      window.removeEventListener('wheel', onScroll);
      window.removeEventListener('touchmove', onScroll);
    };
  }, []);

  const glowing = useMemo(() => computeGlowingIds(posts), [posts]);

  const handleSent = (newPost) => {
    setPosts((prev) => {
      // dedupe by id
      if (prev.some((p) => p.id === newPost.id)) return prev;
      const next = [...prev, newPost];
      writeCachedPosts(next);
      return next;
    });
    scrolledByUserRef.current = false;
    // Background translation takes 5–15s; poll sooner than the 8s interval.
    const refresh = () => {
      try {
        loadRef.current?.();
      } catch (_e) {
        /* no-op */
      }
    };
    [3000, 7000, 14000].forEach((ms) => setTimeout(refresh, ms));
  };

  return (
    <div className="app-frame" data-testid={CHAT.page}>
      {/* SplashScreen, BackgroundGradient, and the canvases are all
          `position: fixed` full-screen layers. They MUST NOT be
          descendants of `.app-shell` because `.app-shell` is
          `overflow-y: auto` — and iOS Safari clips `position: fixed`
          descendants to their nearest scrolling ancestor. That clipping
          is what was leaving a green bar at the bottom (body's solid
          --bg-3 showing where the gradient was cut off) and what made
          the gradient appear to "stop" behind the composer pill. By
          putting them as direct children of the non-scrolling
          `.app-frame` wrapper, they extend correctly to every edge of
          the screen including the safe-area-inset zones. */}
      <SplashScreen />
      {!liteMode && <BackgroundGradient />}
      {!skipCanvases && <SparkleRainCanvas />}
      {!skipCanvases && <CursorTrail />}
      {/* Title is FIXED at the top of the viewport, OUTSIDE
          `.app-shell` so that iOS's rubber-band overscroll inside the
          shell cannot move it. Background-paints the same gradient pixels
          as `.bg-stage` via `.app-title::before`, so the strip looks like
          a continuation of the page gradient. The `.app-page` reserves
          space for it via padding-top. */}
      <h1 className="app-title" data-testid={CHAT.title}>
        danya 🖤 rachel
      </h1>
      {showNotifBtn && (
        <button
          type="button"
          className="notif-fab"
          onClick={alreadySubscribed ? onDisableNotifications : onEnableNotifications}
          disabled={notifBusy}
          data-testid="enable-notifications-btn"
          aria-label={notifBtnLabel}
        >
          <span className="notif-fab__icon" aria-hidden="true">
            {alreadySubscribed ? '🔕' : '🔔'}
          </span>
          <span className="notif-fab__label">{notifBtnLabel}</span>
        </button>
      )}
      <div className="app-shell">
        <div className="app-page">
        <ul className="chat-feed" data-testid={CHAT.feed}>
          {loading && posts.length === 0 && (
            <li className="empty-state" data-testid={CHAT.emptyState}>
              loading…
            </li>
          )}
          {!loading && posts.length === 0 && (
            <li className="empty-state" data-testid={CHAT.emptyState}>
              no posts yet — start the conversation 🖤
            </li>
          )}
          {posts.map((p, idx) => {
            const prev = idx > 0 ? posts[idx - 1] : null;
            const dateChange = prev ? whichDateChanged(prev, p) : 'first';
            return (
              <React.Fragment key={p.id}>
                {dateChange && (
                  <DateSeparator post={p} changedAt={dateChange} />
                )}
                <ChatBubble
                  post={p}
                  isMine={p.device_id === deviceId}
                  isGlowing={glowing.has(p.id)}
                />
              </React.Fragment>
            );
          })}
          <li ref={feedEndRef} aria-hidden="true" />
        </ul>
        </div>
        {/* Composer is INSIDE the scroll container as the LAST child, with
            `position: sticky; bottom: 0` (see App.css). This is the most
            iOS-friendly chat-composer pattern:
              • iOS handles sticky positioning natively during scroll — no
                drift bugs (unlike position: fixed or position: absolute,
                which both got dragged around by iOS Safari's scroll
                tracker in PWA standalone mode).
              • iOS auto-shrinks the layout viewport when the keyboard
                opens (via `interactive-widget=resizes-content` in the
                viewport meta), which shrinks .app-shell, which makes the
                sticky composer end up at the new visible-area bottom =
                flush with the keyboard top. Zero JS, zero compositor
                tricks, zero drift.
            The composer-wrap takes layout space ONLY for its own height
            (the .app-page above uses `min-height: 100%` so an empty feed
            still pushes the composer to the bottom naturally). */}
        <Composer onSent={handleSent} deviceId={deviceId} />
      </div>
      <Toaster richColors position="top-center" />
    </div>
  );
}

function App() {
  // Apply the day/night palette based on local time of day
  // (07:00–20:59 = day, 21:00–06:59 = night). The hook re-evaluates
  // every 60 s and on tab visibility change, so a sleeping phone flips
  // on wake.
  useTimeOfDayTheme();
  // Publish `--app-stable-h` on <html> as the LARGEST window.innerHeight
  // ever observed. Used by the gradient-painting layers so the gradient
  // never compresses when iOS Safari (PWA standalone) collapses every
  // viewport unit during keyboard show. See useStableViewportHeight.js.
  useStableViewportHeight();
  return <Home />;
}

export default App;
